import type { CategoryTreeDto } from "@/types/category.dto";
import type { ProductListDto, ProductResponseDto } from "@/types/product.dto";
import type { StoreListDto, StoreResponseDto } from "@/types/store.dto";
import type { StorefrontCategory, StorefrontProduct, StorefrontStore } from "@/types/storefront";
import { resolveUploadUrl } from "@/services/filesService";

const DEFAULT_PRODUCT_IMAGE = "";
const DEFAULT_STORE_IMAGE =
  "https://images.unsplash.com/photo-1604719312566-8912e9227c6a?w=400&h=300&fit=crop&fm=webp";

const CATEGORY_COLORS = [
  "hsl(155 65% 32%)",
  "hsl(210 80% 52%)",
  "hsl(28 90% 55%)",
  "hsl(0 72% 51%)",
  "hsl(38 92% 50%)",
  "hsl(280 60% 50%)",
  "hsl(190 70% 42%)",
  "hsl(330 70% 55%)",
];

const CATEGORY_ICONS: Record<string, string> = {
  "fruits & vegetables": "🥬",
  "dairy & eggs": "🥛",
  bakery: "🍞",
  "meat & fish": "🍖",
  snacks: "🍿",
  beverages: "🧃",
  household: "🧹",
  "personal care": "🧴",
};

function categoryIcon(name: string): string {
  return CATEGORY_ICONS[name.toLowerCase()] ?? "🛒";
}

function formatDistance(km?: number): string {
  if (km == null) return "—";
  if (km < 1) return `${Math.round(km * 1000)} m`;
  return `${km.toFixed(1)} km`;
}

function estimateDeliveryTime(km?: number): string {
  if (km == null) return "20-30 min";
  if (km < 1) return "15-20 min";
  if (km < 3) return "20-30 min";
  return "30-45 min";
}

function parseTags(tags?: string): string[] | undefined {
  if (!tags) return undefined;
  const parsed = tags.split(",").map((t) => t.trim()).filter(Boolean);
  return parsed.length ? parsed : undefined;
}

export function mapProductListDtoToStorefront(dto: ProductListDto): StorefrontProduct {
  return {
    id: String(dto.id),
    name: dto.name,
    description: "",
    price: dto.sellingPrice,
    originalPrice: dto.discountPercentage > 0 ? dto.mrp : undefined,
    businessVolume: dto.businessVolume,
    image: resolveUploadUrl(dto.primaryImage ?? DEFAULT_PRODUCT_IMAGE),
    category: dto.categoryName,
    unit: "piece",
    rating: dto.rating,
    reviewCount: 0,
    storeId: String(dto.storeId),
    storeName: dto.storeName,
    inStock: dto.inStock,
    tags: dto.isVeg ? ["Veg", ...(parseTags(undefined) ?? [])] : parseTags(undefined),
  };
}

export function mapProductResponseDtoToStorefront(dto: ProductResponseDto): StorefrontProduct {
  const tags = parseTags(dto.tags);
  return {
    id: String(dto.id),
    name: dto.name,
    description: dto.description ?? dto.shortDescription ?? "",
    price: dto.sellingPrice,
    originalPrice: dto.discountPercentage > 0 ? dto.mrp : undefined,
    businessVolume: dto.businessVolume,
    image: resolveUploadUrl(dto.primaryImage ?? dto.images?.[0]?.imageUrl ?? DEFAULT_PRODUCT_IMAGE),
    category: dto.categoryName,
    unit: dto.unit,
    rating: dto.rating,
    reviewCount: dto.reviewCount,
    storeId: String(dto.storeId),
    storeName: dto.storeName,
    inStock: dto.inStock,
    tags: dto.isVeg ? ["Veg", ...(tags ?? [])] : tags,
  };
}

export function mapStoreListDtoToStorefront(dto: StoreListDto): StorefrontStore {
  return {
    id: String(dto.id),
    name: dto.storeName,
    description: "",
    image: resolveUploadUrl(dto.logoUrl ?? DEFAULT_STORE_IMAGE),
    rating: dto.rating,
    reviewCount: dto.totalOrders,
    distance: formatDistance(dto.distance),
    deliveryTime: estimateDeliveryTime(dto.distance),
    deliveryFee: 20,
    categories: [],
    isOpen: dto.isOpen,
    address: dto.city,
    productCount: 0,
  };
}

export function mapStoreResponseDtoToStorefront(dto: StoreResponseDto): StorefrontStore {
  return {
    id: String(dto.id),
    name: dto.storeName,
    description: dto.description ?? "",
    image: resolveUploadUrl(dto.logoUrl ?? dto.bannerUrl ?? DEFAULT_STORE_IMAGE),
    rating: dto.rating,
    reviewCount: dto.totalOrders,
    distance: formatDistance(dto.distance),
    deliveryTime: estimateDeliveryTime(dto.distance),
    deliveryFee: dto.deliveryCharge,
    categories: [],
    isOpen: dto.isOpen,
    address: dto.fullAddress,
    productCount: 0,
  };
}

export function mapCategoryTreeToStorefront(categories: CategoryTreeDto[]): StorefrontCategory[] {
  return categories.map((cat, index) => ({
    id: String(cat.id),
    name: cat.name,
    icon: categoryIcon(cat.name),
    imageUrl: cat.imageUrl ? resolveUploadUrl(cat.imageUrl) : undefined,
    productCount: cat.productCount,
    color: CATEGORY_COLORS[index % CATEGORY_COLORS.length],
  }));
}

export function mapProductListDtosToStorefront(items: ProductListDto[]): StorefrontProduct[] {
  return items.map(mapProductListDtoToStorefront);
}

export function mapStoreListDtosToStorefront(items: StoreListDto[]): StorefrontStore[] {
  return items.map(mapStoreListDtoToStorefront);
}
